import { Link, usePage } from '@inertiajs/react'
import type { ComponentType, HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type SidebarProps = HTMLAttributes<HTMLElement> & {
  collapsed: boolean
}

function Sidebar({ className, collapsed, children, ...props }: SidebarProps) {
  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'group fixed inset-y-0 left-0 z-20 flex flex-col border-r border-border bg-card text-card-foreground transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
      {...props}
    >
      {children}
    </aside>
  )
}

function SidebarHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex h-16 items-center gap-3 border-b border-border px-3', className)}
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex-1 overflow-y-auto px-2 py-3', className)} {...props} />
}

function SidebarFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('border-t border-border p-2', className)}
      {...props}
    />
  )
}

function SidebarSectionLabel({ children, collapsed }: { children: ReactNode; collapsed: boolean }) {
  if (collapsed) return null
  return (
    <p className="mb-1 mt-3 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
      {children}
    </p>
  )
}

type SidebarItemProps = {
  icon: ComponentType<{ className?: string }>
  label: string
  href?: string
  matchExact?: boolean
  collapsed: boolean
  disabled?: boolean
}

function SidebarItem({
  icon: Icon,
  label,
  href,
  matchExact = false,
  collapsed,
  disabled = false,
}: SidebarItemProps) {
  const { url } = usePage()
  const currentPath = url.split('?')[0]
  const active = !disabled && href
    ? matchExact
      ? currentPath === href
      : currentPath === href || currentPath.startsWith(`${href}/`)
    : false

  const baseClass = cn(
    'flex h-9 items-center gap-3 rounded-md px-2 text-sm font-medium transition-colors',
    collapsed && 'justify-center',
    active
      ? 'bg-primary text-primary-foreground'
      : disabled
        ? 'cursor-not-allowed text-muted-foreground/60'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
  )

  const content = (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </>
  )

  if (disabled || !href) {
    return (
      <span className={baseClass} title={collapsed ? label : undefined} aria-disabled={disabled}>
        {content}
      </span>
    )
  }

  return (
    <Link className={baseClass} href={href} title={collapsed ? label : undefined}>
      {content}
    </Link>
  )
}

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarItem,
  SidebarSectionLabel,
}
